import React, { useState } from "react";
import { LabService, type Lab } from "../services/labService";
import { Alert } from "./Alert";

interface EditLabModalProps {
  lab: Lab;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (updatedLab: Lab) => void;
}

export const EditLabModal: React.FC<EditLabModalProps> = ({
  lab,
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [fte, setFte] = useState<string>(String(lab.total_fte));
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const dialogRef = React.useRef<HTMLDialogElement>(null);

  React.useEffect(() => {
    if (isOpen) {
      dialogRef.current?.showModal();
    } else {
      dialogRef.current?.close();
    }
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      const fteNum = parseFloat(fte);
      if (!fte || isNaN(fteNum) || fteNum <= 0) {
        throw new Error("Total FTE must be a positive number");
      }

      const response = await LabService.updateLab(lab.id, {
        total_fte: fteNum,
      });

      onSuccess(response.lab);
      onClose();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <dialog 
      ref={dialogRef} 
      className="modal bg-black/40 backdrop-blur-[2px]"
      onClose={onClose}
    >
      <div className="modal-box bg-amber-50 border border-amber-200 shadow-2xl overflow-x-hidden max-w-md w-full p-6 sm:p-8 rounded-2xl">
        <h3 className="font-bold text-2xl mb-2 text-green-700 leading-tight">
          Edit Lab Details
        </h3>
        <p className="text-base-content/70 text-sm mb-6 pb-2 border-b border-amber-200">
          Updating details for <span className="font-semibold text-amber-800">{lab.name}</span>
        </p>
        
        {error && <Alert type="error" message={error} />}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="form-control w-full">
            <label className="label pt-0">
              <span className="label-text font-semibold text-emerald-900">Total FTE (Full-Time Equivalent)</span>
            </label>
            <input
              type="number"
              step="0.1"
              min="0.1"
              className="input input-bordered w-full focus:input-primary border-amber-300 bg-white/50 focus:bg-white transition-all duration-200 rounded-xl"
              value={fte}
              onChange={(e) => setFte(e.target.value)}
              required
              autoFocus
            />
            <label className="label">
              <span className="label-text-alt text-base-content/60 italic text-wrap">
                Update the total number of full-time equivalent staff in this lab.
              </span>
            </label>
          </div>

          <div className="modal-action gap-3 mt-8">
            <button
              type="button"
              className="btn btn-outline border-amber-300 hover:bg-amber-100 hover:border-amber-400 text-amber-800 rounded-xl px-6 min-h-[3rem]"
              onClick={onClose}
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary rounded-xl px-8 min-h-[3rem] shadow-md hover:shadow-lg transition-all duration-300"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <span className="loading loading-spinner loading-xs"></span>
                  Saving...
                </>
              ) : (
                "Save Changes"
              )}
            </button>
          </div>
        </form>
      </div>
      <form method="dialog" className="modal-backdrop bg-transparent border-none">
        <button className="cursor-default outline-none">close</button>
      </form>
    </dialog>
  );
};
